export type GA4Params = Record<string, string | number | boolean | null | undefined | object>;

/**
 * sessionStorage guard so the synthetic submit_customer_information fired for
 * auto-signed-in (launcher / chauffeur) customers happens at most once per
 * booking. Cleared by dataContext.clearSelections() with the other booking keys.
 */
export const AUTO_CUSTOMER_INFO_KEY = 'ga4_auto_customer_info';

export const ga4Event = (
  eventName: string,
  params: GA4Params = {},
  opts?: { gtagTracking?: boolean; appContext?: string }
) => {
  const enabled = opts?.gtagTracking ?? (window as any).gtagTracking ?? true;
  const app = opts?.appContext ?? (window as any).appContext ?? "production";
  if (app !== "production" || !enabled) {
    console.log(`GA4 not sent [${app}, tracking=${enabled}] event=${eventName} params=${JSON.stringify(params)}`);
    return;
  }
  // gtag would otherwise attach the raw address (e.g. the Paytrail return a purchase fires on).
  (window as any).gtag?.("event", eventName, { ...params, page_location: ga4PageLocationParam(params.page_location) });
};

// Set GA4 user-scoped properties (e.g. vehicle_segment, surface) so they attach to the
// session and every subsequent event — enables user-level funnel breakdowns.
export const ga4SetUserProperties = (
  props: Record<string, string | number | boolean | null | undefined>,
  opts?: { gtagTracking?: boolean; appContext?: string }
) => {
  const enabled = opts?.gtagTracking ?? (window as any).gtagTracking ?? true;
  const app = opts?.appContext ?? (window as any).appContext ?? "production";
  if (app !== "production" || !enabled) {
    console.log(`GA4 not sent [${app}, tracking=${enabled}] set user_properties=${JSON.stringify(props)}`);
    return;
  }
  (window as any).gtag?.("set", "user_properties", props);
};

/**
 * Set the GA4 User-ID.
 *
 * This is the piece that stitches the launcher's NATIVE stream (which already
 * calls `analytics().setUserId(customer.id)` in ReactNativeFE App.tsx) to this
 * WEBVIEW's web stream. Without it the same person books as two different GA4
 * users — one in the app stream, one in the web stream — which is why the app
 * platform report and the browser report disagree.
 *
 * Requires GA4 reporting identity = Blended (or Observed) to take effect.
 * The value must be the 24rent customer id and nothing else: never an email,
 * phone number or other PII (Google prohibits it and it would be a GDPR issue).
 */
export const ga4SetUserId = (
  userId: string | null,
  opts?: { gtagTracking?: boolean; appContext?: string }
) => {
  const enabled = opts?.gtagTracking ?? (window as any).gtagTracking ?? true;
  const app = opts?.appContext ?? (window as any).appContext ?? "production";
  if (app !== "production" || !enabled) {
    console.log(`GA4 not sent [${app}, tracking=${enabled}] set user_id=${userId}`);
    return;
  }
  (window as any).gtag?.("set", { user_id: userId });
};

export const virtualPageViews: Record<string, { title: string; path: string }> = {
  'search': { title: 'RRM – a2a Search', path: '/rrm/search/' },
  'onewaySearch': { title: 'RRM – One-Way Search', path: '/rrm/oneway-search/' },
  'selectServices': { title: 'RRM – Additional Services', path: '/rrm/select-services/' },
  'customerInfo': { title: 'RRM – Customer Information', path: '/rrm/customer-info/' },
  'summary': { title: 'RRM – Summary and Payment', path: '/rrm/summary/' },
  'paymentProvider': { title: 'RRM – Payment Provider Selection', path: '/rrm/payment-provider/' },
  'paymentSuccess': { title: 'RRM – Reservation Success', path: '/rrm/success' },
  'quotaFreeSuccess': { title: 'RRM – Reservation Success', path: '/rrm/quota-or-free-success/' },
  'paymentFailure': { title: 'RRM – Reservation Failure', path: '/rrm/failure' },
  'reservationCancelled': { title: 'RRM – Reservation Cancelled', path: '/rrm/cancelled' },
};

// Query parameters that never go to GA4: the Paytrail return's `h` and Paytrail's own
// `signature` / `checkout-*` (PLATFORM-2312), and the login `hash` of `?id=&hash=` links.
// index.html's gtag config applies the same rule to GA4's automatic page_view: keep both in sync.
const GA4_DROPPED_PARAMS = ['h', 'signature', 'hash', 'token'];

/**
 * The page address for GA4, without the parameters above. The query string is filtered as
 * text, so every other parameter keeps its exact encoding.
 */
export function ga4PageLocation(href: string): string {
  const match = /^([^?#]*)\?([^#]*)(#.*)?$/.exec(href);
  if (!match) return href;
  const [, base, query, fragment = ''] = match;
  const pairs = query.split('&');
  const kept = pairs.filter(pair => {
    const name = decodeParamName(pair.split('=')[0]).toLowerCase();
    return !GA4_DROPPED_PARAMS.includes(name) && !name.startsWith('checkout-');
  });
  if (kept.length === pairs.length) return href;
  return base + (kept.length ? `?${kept.join('&')}` : '') + fragment;
}

// `%68` is `h` too. A name that doesn't decode is compared as it is.
function decodeParamName(raw: string): string {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

/** A caller's own page_location is cleaned like the current address. */
const ga4PageLocationParam = (given: unknown): string =>
  ga4PageLocation(typeof given === 'string' ? given : window.location.href);

export function sendVirtualPageView(
  path: string,
  title?: string,
  opts?: { gtagTracking?: boolean; appContext?: string; params?: GA4Params }
) {
  const enabled = opts?.gtagTracking ?? (window as any).gtagTracking ?? true;
  const app = opts?.appContext ?? (window as any).appContext ?? "production";
  // virtual_pageview is the RRM's step event, so it needs the same surface stamp
  // as every other event — otherwise the step-by-step funnel cannot be split by
  // launcher vs browser even though the individual events can.
  const payload = {
    page_path: path,
    page_title: title || document.title,
    ...(opts?.params ?? {}),
    page_location: ga4PageLocationParam(opts?.params?.page_location),
  };
  if (app !== "production" || !enabled) {
    console.log(`%cGA4 not sent [${app}, tracking=${enabled}] event=virtual_pageview params=${JSON.stringify(payload)}`,
      'color: #2196f3; font-weight: bold;');
    return;
  }
  if ((window as any).gtag) {
    (window as any).gtag('event', 'virtual_pageview', payload);
  }
}